import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from 'src/shared/components/ui/sheet';
import { Input } from 'src/shared/components/ui/input';
import { Button } from 'src/shared/components/ui/button';
import { GraphApiError } from 'src/shared/state/api-slices/graphApiSlice';
import { useCreateCustomerMutation } from 'src/app/admin/state/api/adminGraphSlice';

type AddOrganisationSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const DUPLICATE_CUSTOMER_ID_CODE = 'DUPLICATE_CUSTOMER_ID';

const getErrorMessage = (error: unknown): string => {
    if ((error as GraphApiError)?.failures?.some(failure => failure.code === DUPLICATE_CUSTOMER_ID_CODE)) {
        return 'An organisation with that Customer ID already exists.';
    }
    return 'Something went wrong creating the organisation. Please try again.';
};

const AddOrganisationSheet = ({ open, onOpenChange }: AddOrganisationSheetProps) => {
    const [customerId, setCustomerId] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [createCustomer, { isLoading }] = useCreateCustomerMutation();

    const canSubmit = customerId.trim() !== '' && customerName.trim() !== '' && !isLoading;

    const resetForm = () => {
        setCustomerId('');
        setCustomerName('');
        setErrorMessage(null);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            resetForm();
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setErrorMessage(null);
        try {
            await createCustomer({ customerId: customerId.trim(), customerName: customerName.trim() }).unwrap();
            resetForm();
            onOpenChange(false);
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>Add organisation</SheetTitle>
                    <SheetDescription>Create a new customer organisation on the platform.</SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className='flex flex-col gap-4 mt-6' data-testid='add-organisation-form'>
                    <div className='flex flex-col gap-1'>
                        <label htmlFor='org-customer-id' className='text-sm font-medium'>
                            Customer ID
                        </label>
                        <Input
                            id='org-customer-id'
                            value={customerId}
                            onChange={event => setCustomerId(event.target.value)}
                            placeholder='e.g. C00123'
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label htmlFor='org-customer-name' className='text-sm font-medium'>
                            Customer name
                        </label>
                        <Input
                            id='org-customer-name'
                            value={customerName}
                            onChange={event => setCustomerName(event.target.value)}
                            placeholder='Organisation name'
                        />
                    </div>

                    {errorMessage && (
                        <p role='alert' className='text-sm text-red-100'>
                            {errorMessage}
                        </p>
                    )}

                    <SheetFooter className='mt-2'>
                        <Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type='submit' disabled={!canSubmit} className='flex-grow'>
                            Add organisation
                        </Button>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
};

export default AddOrganisationSheet;
